<template>
  <div>
    <h1 v-if="name" class="mb-3 has-text-weight-bold is-size-5">{{ name }}</h1>
    <b-modal :active="isTranslationsModalVisible" :can-cancel="true" @close="closeTranslationsModal">
      <div class="has-background-white-ter p-4">
        <h3 class="is-size-5 has-text-weight-semibold mb-4">
          {{ descriptionForm.id ? "Редактировать перевод" : "Добавить перевод" }}
        </h3>
        <form @submit.prevent="saveDescription">
          <div class="columns is-multiline">
            <div class="column is-one-quarter">
              <b-field
                label="Язык"
                :type="descriptionErrors.lang_code ? 'is-danger' : ''"
                :message="descriptionErrors.lang_code && descriptionErrors.lang_code[0] || ''"
              >
                <b-select v-model="descriptionForm.lang_code" expanded>
                  <option v-for="lang in supportedLangs" :key="lang" :value="lang">{{ lang }}</option>
                </b-select>
              </b-field>
            </div>
            <div class="column is-three-quarters">
              <b-field
                label="Название"
                :type="descriptionErrors.name ? 'is-danger' : ''"
                :message="descriptionErrors.name && descriptionErrors.name[0] || ''"
              >
                <b-input v-model="descriptionForm.name" maxlength="255" />
              </b-field>
            </div>
            <div class="column is-full">
              <b-field
                label="Описание"
                :type="descriptionErrors.description ? 'is-danger' : ''"
                :message="descriptionErrors.description && descriptionErrors.description[0] || ''"
              >
                <b-input v-model="descriptionForm.description" type="textarea" />
              </b-field>
            </div>
            <div class="column is-full is-flex is-justify-content-end">
              <b-button class="mr-2" :disabled="isDescriptionSaving" @click="closeTranslationsModal">Отмена</b-button>
              <b-button type="is-info" native-type="submit" :loading="isDescriptionSaving">
                {{ descriptionForm.id ? "Сохранить" : "Добавить" }}
              </b-button>
            </div>
          </div>
        </form>
      </div>
    </b-modal>
    <b-tabs v-model="activeTab" type="is-boxed">
      <b-tab-item label="CMS"><CMSEdit :channel-list.sync="channelList" @setName="setName" /></b-tab-item>
      <b-tab-item label="Спектры скорости"><VelEdit :agent-id="id" :channels="channelList" /></b-tab-item>
      <b-tab-item label="Спектры ускорения"><AccEdit :agent-id="id" :channels="channelList" /></b-tab-item>
      <b-tab-item label="Спектры огибающей"><EnvEdit :agent-id="id" :channels="channelList" /></b-tab-item>
      <b-tab-item label="Настройка температуры"><TemperatureEdit :agent-id="id" :channels="channelList" :is-active="activeTab === 4" /></b-tab-item>
      <b-tab-item label="Watchdogs"><WatchdogEdit :agent-id="id" :channels="channelList" :is-active="activeTab === 5" /></b-tab-item>
      <b-tab-item label="Minions"><MinionEdit :agent-id="id" :channels="channelList" :is-active="activeTab === 6" /></b-tab-item>
      <b-tab-item label="Переводы">
        <div class="has-background-white px-4 py-4">
          <div class="is-flex is-justify-content-space-between is-align-items-center mb-4">
            <h4 class="is-size-6 has-text-weight-medium">Переводы CMS</h4>
            <b-button type="is-info" @click="openCreateDescriptionModal">Добавить</b-button>
          </div>
          <b-table
            :data="descriptions"
            :loading="isTranslationsLoading"
            hoverable
            striped
            @cellclick="openEditDescriptionModal"
          >
            <b-table-column field="id" label="ID" width="40" numeric v-slot="props">
              {{ props.row.id }}
            </b-table-column>
            <b-table-column field="lang_code" label="Язык" v-slot="props">
              {{ props.row.lang_code }}
            </b-table-column>
            <b-table-column field="name" label="Название" v-slot="props">
              {{ props.row.name || '-' }}
            </b-table-column>
            <b-table-column field="description" label="Описание" v-slot="props">
              {{ props.row.description || '-' }}
            </b-table-column>
            <b-table-column label="Удалить" centered v-slot="props">
              <b-button
                type="is-danger is-light"
                size="is-small"
                icon-right="trash"
                @click.stop="deleteDescription(props.row.id)"
              />
            </b-table-column>
          </b-table>
          <section v-if="descriptionsTotal > descriptionsPerPage" class="mt-4">
            <b-pagination
              :total="descriptionsTotal"
              :per-page="descriptionsPerPage"
              order="is-centered"
              icon-pack="fas"
              :current="descriptionsPage"
              class="base-table__pagination"
              @change="fetchDescriptions"
            />
          </section>
        </div>
      </b-tab-item>
      <b-tab-item label="Features"><FeatureEdit :agent-id="id" :channels="channelList" :is-active="activeTab === 8" /></b-tab-item>
      <b-tab-item label="Feature values"><FeatureValuesEdit :agent-id="id" :is-active="activeTab === 9" /></b-tab-item>
      <b-tab-item label="Trend Names"><TrendNamesEdit :agent-id="id" :channels="channelList" :is-active="activeTab === 10" /></b-tab-item>
    </b-tabs>
  </div>
</template>

<script>
import "vue-advanced-cropper/dist/style.css";
import CMSEdit from "@/components/CMS/Sub-component/CMSEdit.vue"
import VelEdit from "@/components/CMS/Sub-component/VelEdit.vue"
import AccEdit from "@/components/CMS/Sub-component/AccEdit.vue"
import EnvEdit from "@/components/CMS/Sub-component/EnvEdit.vue"
import TemperatureEdit from "@/components/CMS/Sub-component/Temperature/TemperatureEdit.vue"
import WatchdogEdit from "@/components/CMS/Sub-component/Watchdog/WatchdogEdit.vue"
import MinionEdit from "@/components/CMS/Sub-component/Minion/MinionEdit.vue"
import FeatureEdit from "@/components/CMS/Sub-component/Feature/FeatureEdit.vue"
import FeatureValuesEdit from "@/components/CMS/Sub-component/Feature/FeatureValuesEdit.vue"
import TrendNamesEdit from "@/components/CMS/Sub-component/TrendNames/TrendNamesEdit.vue"

export default {
  components: { MinionEdit, WatchdogEdit, TemperatureEdit, EnvEdit, AccEdit, VelEdit, CMSEdit, FeatureEdit, FeatureValuesEdit, TrendNamesEdit },
  props: ['id'],
  data() {
    return {
      channelList: [],
      activeTab: 0,
      name: "",
      isTranslationsModalVisible: false,
      isTranslationsLoading: false,
      isDescriptionSaving: false,
      descriptions: [],
      descriptionsTotal: 0,
      descriptionsPage: 1,
      descriptionsPerPage: 10,
      supportedLangs: ["ru", "kk", "en"],
      descriptionForm: {
        id: null,
        lang_code: "ru",
        name: "",
        description: "",
      },
      descriptionErrors: {},
    };
  },
  watch: {
    activeTab(val) {
      if (val === 7) this.fetchDescriptions(this.descriptionsPage)
    },
  },
  methods: {
    setName(event) {
      this.name = event
    },
    openCreateDescriptionModal() {
      this.resetDescriptionForm()
      this.isTranslationsModalVisible = true
    },
    openEditDescriptionModal(item) {
      this.descriptionErrors = {}
      this.descriptionForm = {
        id: item.id,
        lang_code: item.lang_code || "ru",
        name: item.name || "",
        description: item.description || "",
      }
      this.isTranslationsModalVisible = true
    },
    closeTranslationsModal() {
      this.isTranslationsModalVisible = false
      this.resetDescriptionForm()
    },
    resetDescriptionForm() {
      this.descriptionErrors = {}
      this.descriptionForm = {
        id: null,
        lang_code: "ru",
        name: "",
        description: "",
      }
    },
    async fetchDescriptions(page = 1) {
      this.descriptionsPage = page || 1
      this.isTranslationsLoading = true
      // const offset = (this.descriptionsPage - 1) * this.descriptionsPerPage
      try {
        const { data } = await this.$axios.get(
          `admin/agent-descriptions?agent_id=${this.id}`,
          {
            params: { agent_id: this.id },
          }
        )
        this.descriptions = data?.data || []
        this.descriptionsTotal = data?.count || 0
      } catch (e) {
        throw new Error(e)
      } finally {
        this.isTranslationsLoading = false
      }
    },
    async saveDescription() {
      this.descriptionErrors = {}
      this.isDescriptionSaving = true
      const payload = {
        agent_id: this.id,
        lang_code: this.descriptionForm.lang_code,
        name: this.descriptionForm.name || null,
        description: this.descriptionForm.description || null,
      }
      try {
        if (this.descriptionForm.id) {
          await this.$axios.put(`admin/agent-descriptions/${this.descriptionForm.id}`, payload)
        } else {
          await this.$axios.post("admin/agent-descriptions", payload)
        }
        await this.fetchDescriptions(this.descriptionsPage)
        this.closeTranslationsModal()
      } catch (e) {
        this.descriptionErrors = e.error?.response?.data?.errors ?? {}
        throw e
      } finally {
        this.isDescriptionSaving = false
      }
    },
    async deleteDescription(id) {
      await this.$axios.delete(`admin/agent-descriptions/${id}`)
      await this.fetchDescriptions(this.descriptionsPage)
      if (this.descriptionForm.id === id) this.resetDescriptionForm()
    },
  }
};
</script>
